import React, { useEffect, useMemo, useState } from "react";
import { TextField, MenuItem, TablePagination } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import adminService from "../../services/adminService.ts";
import AlterarLiberacaoModal from "../../components/Modals/AlterarLiberacaoModal/index.tsx";
import ExcluirEmpresaModal from "../../components/Modals/ExcluirEmpresaModal/index.tsx";
import { formatCNPJ } from "../../utils/cnpj.util.ts";
import Badge, { BadgeColor } from "../../ui/Badge.tsx";
import StatTile from "../../ui/StatTile.tsx";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import "../../styles/design-system.css";

interface EmpresaAdmin {
  id: string;
  nome: string;
  cnpj: string;
  email: string;
  planoModeloId: string | null;
  planoNome: string | null;
  ciclo: string | null;
  dataExpiracao: string | null;
  pago: boolean;
  planoAtivo: boolean;
}

type StatusConta = "ativo" | "trial" | "inadimplente" | "sem-plano";

const STATUS_INFO: Record<StatusConta, { label: string; color: BadgeColor }> = {
  ativo: { label: "Ativo", color: "green" },
  trial: { label: "Trial", color: "blue" },
  inadimplente: { label: "Inadimplente", color: "red" },
  "sem-plano": { label: "Sem plano", color: "neutral" },
};

const isExpirada = (dataExpiracao: string | null) => {
  if (!dataExpiracao) return false;
  return new Date(dataExpiracao + "T23:59:59") < new Date();
};

// Mesmo critério de "conta paga de verdade" usado no resto do sistema
// (MeuPlano/GestaoUsuarios): pago=true só é setado pelo webhook real da
// ASAAS. dataExpiracao é o que de fato controla acesso (ver
// AcessoBloqueadoInterceptor) — muitas empresas reais têm dataExpiracao de
// trial preenchida sem nenhum Plano formalmente atribuído ainda
// (planoNome null), então o status usa dataExpiracao, não planoNome.
// "Sem plano" fica só pra quando não existe NENHUMA liberação registrada
// (dataExpiracao nula — normalmente contas internas ControlSIS).
const classificarStatus = (empresa: EmpresaAdmin): StatusConta => {
  if (!empresa.dataExpiracao) return "sem-plano";
  if (isExpirada(empresa.dataExpiracao)) return "inadimplente";
  if (empresa.pago) return "ativo";
  return "trial";
};

const cicloLabel = (ciclo: string | null) => {
  if (ciclo === "MONTHLY") return "Mensal";
  if (ciclo === "YEARLY") return "Anual";
  return null;
};

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const formatarData = (iso: string | null) => {
  if (!iso) return "—";
  const [ano, mes, dia] = iso.split("-").map(Number);
  if (!ano || !mes || !dia) return "—";
  return `${String(dia).padStart(2, "0")} ${MESES[mes - 1]} ${ano}`;
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiSvgIcon-root": { color: "var(--ink-faint)" },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "var(--surface)",
      color: "var(--ink)",
      border: "1px solid var(--border)",
      "& .MuiMenuItem-root:hover": { bgcolor: "var(--surface-2)" },
      "& .MuiMenuItem-root.Mui-selected": { bgcolor: "var(--surface-2)" },
      "& .MuiMenuItem-root.Mui-selected:hover": { bgcolor: "var(--surface-2)" },
    },
  },
};

const paginationSx = {
  color: "var(--ink-soft)",
  "& .MuiTablePagination-toolbar": { paddingLeft: "6px", flexWrap: "wrap", rowGap: "6px" },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    color: "var(--ink-faint)", fontSize: 12.8, margin: 0,
  },
  "& .MuiTablePagination-select": { color: "var(--ink)" },
  "& .MuiSvgIcon-root": { color: "var(--ink-faint)" },
  "& .MuiIconButton-root": { color: "var(--ink-soft)" },
  "& .MuiIconButton-root.Mui-disabled": { color: "var(--border-strong)" },
};

const PainelAdministrativo = () => {
  const [empresas, setEmpresas] = useState<EmpresaAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<EmpresaAdmin | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [empresaParaExcluir, setEmpresaParaExcluir] = useState<EmpresaAdmin | null>(null);
  const [isExcluirModalOpen, setIsExcluirModalOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState<StatusConta | "todos">("todos");
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(10);

  const fetchEmpresas = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.listarEmpresas();
      setEmpresas(Array.isArray(data) ? data : []);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmpresas();
  }, []);

  const handleGerenciar = (empresa: EmpresaAdmin) => {
    setEmpresaSelecionada(empresa);
    setIsModalOpen(true);
  };

  const handleUpdate = (empresaAtualizada: EmpresaAdmin) => {
    setEmpresas((prev) => prev.map((e) => (e.id === empresaAtualizada.id ? { ...e, ...empresaAtualizada } : e)));
    setEmpresaSelecionada((prev) => (prev ? { ...prev, ...empresaAtualizada } : prev));
  };

  const handleExcluir = (empresa: EmpresaAdmin) => {
    setEmpresaParaExcluir(empresa);
    setIsExcluirModalOpen(true);
  };

  const handleExcluida = (empresaId: string) => {
    setEmpresas((prev) => prev.filter((e) => e.id !== empresaId));
  };

  const stats = useMemo(() => {
    const contadores = { ativo: 0, trial: 0, inadimplente: 0, "sem-plano": 0 } as Record<StatusConta, number>;
    empresas.forEach((e) => { contadores[classificarStatus(e)]++; });
    return { total: empresas.length, ...contadores };
  }, [empresas]);

  const termoBusca = busca.trim().toLowerCase();
  const termoBuscaDigitos = termoBusca.replace(/\D/g, "");
  const empresasFiltradas = empresas.filter((e) => {
    // "".includes("") é sempre true em JS — sem o guard de termoBuscaDigitos,
    // buscar um termo sem nenhum dígito (ex: "teste") fazia a cláusula do
    // CNPJ "bater" em qualquer empresa e mascarar o filtro de nome/e-mail.
    const bateBusca = !termoBusca
      || e.nome.toLowerCase().includes(termoBusca)
      || e.email.toLowerCase().includes(termoBusca)
      || (!!termoBuscaDigitos && e.cnpj.replace(/\D/g, "").includes(termoBuscaDigitos));
    const bateStatus = statusFiltro === "todos" || classificarStatus(e) === statusFiltro;
    return bateBusca && bateStatus;
  });

  // Volta pra primeira página sempre que o filtro muda o resultado — senão
  // dá pra "sumir" numa página vazia depois de buscar/filtrar.
  useEffect(() => {
    setPagina(0);
  }, [termoBusca, statusFiltro]);

  const empresasPaginadas = empresasFiltradas.slice(
    pagina * linhasPorPagina,
    pagina * linhasPorPagina + linhasPorPagina
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Painel Administrativo <Badge color="neutral">
            <LockOutlinedIcon style={{ width: 12, height: 12 }} /> Acesso ControlSIS
          </Badge></h1>
          <p>Gestão interna das empresas clientes — liberação manual, alteração de plano e exclusão de conta.</p>
        </div>
      </div>

      <div className="ds-stat-grid">
        <StatTile
          label="Empresas cadastradas" value={stats.total}
          icon={<ApartmentOutlinedIcon fontSize="small" />} color="navy"
          foot="Todas as contas"
        />
        <StatTile
          label="Ativas" value={stats.ativo}
          icon={<CheckCircleOutlineIcon fontSize="small" />} color="green"
          foot="Assinatura em dia"
        />
        <StatTile
          label="Em trial" value={stats.trial}
          icon={<AccessTimeOutlinedIcon fontSize="small" />} color="blue"
          foot="Período gratuito"
        />
        <StatTile
          label="Inadimplentes" value={stats.inadimplente}
          icon={<CancelOutlinedIcon fontSize="small" />} color="red"
          foot="Acesso bloqueado"
        />
      </div>

      <div className="ds-toolbar" style={{ justifyContent: "space-between" }}>
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome, e-mail ou CNPJ..." />
        <TextField
          select size="small" label="Status" value={statusFiltro}
          onChange={(e) => setStatusFiltro(e.target.value as StatusConta | "todos")}
          sx={{ ...fieldSx, minWidth: 150 }}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          <MenuItem value="todos">Todos</MenuItem>
          <MenuItem value="ativo">Ativo</MenuItem>
          <MenuItem value="trial">Trial</MenuItem>
          <MenuItem value="inadimplente">Inadimplente</MenuItem>
          <MenuItem value="sem-plano">Sem plano</MenuItem>
        </TextField>
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Plano</th>
              <th>Status</th>
              <th>Expira em</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={5} />
            ) : empresasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    title={termoBusca ? `Nenhuma empresa encontrada para "${busca}"` : "Nenhuma empresa cadastrada"}
                  />
                </td>
              </tr>
            ) : (
              empresasPaginadas.map((empresa) => {
                const status = classificarStatus(empresa);
                const { label, color } = STATUS_INFO[status];
                return (
                  <tr key={empresa.id}>
                    <td className="ds-cell-title">
                      {empresa.nome}
                      <div style={{ fontSize: 12, color: "var(--ink-faint)", fontWeight: 500, marginTop: 2 }}>
                        {formatCNPJ(empresa.cnpj)} · {empresa.email}
                      </div>
                    </td>
                    <td>
                      {empresa.planoNome || "Sem plano"}
                      {cicloLabel(empresa.ciclo) && (
                        <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 2 }}>
                          {cicloLabel(empresa.ciclo)}
                        </div>
                      )}
                    </td>
                    <td><Badge color={color}>{label}</Badge></td>
                    <td>{formatarData(empresa.dataExpiracao)}</td>
                    <td>
                      <div className="ds-row-actions">
                        <Button variant="outline" size="sm" onClick={() => handleGerenciar(empresa)}>
                          Alterar plano
                        </Button>
                        <button className="ds-icon-btn" onClick={() => handleExcluir(empresa)} title="Excluir empresa" style={{ color: "var(--red)" }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!isLoading && empresasFiltradas.length > 0 && (
          <TablePagination
            component="div"
            count={empresasFiltradas.length}
            page={pagina}
            onPageChange={(_, novaPagina) => setPagina(novaPagina)}
            rowsPerPage={linhasPorPagina}
            onRowsPerPageChange={(e) => {
              setLinhasPorPagina(parseInt(e.target.value, 10));
              setPagina(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage="Empresas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            SelectProps={{ MenuProps: selectMenuProps }}
            sx={{ ...paginationSx, borderTop: "1px solid var(--border)", px: 1 }}
          />
        )}
      </div>

      <div className="ds-card" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 18px", marginTop: 16 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: "var(--ink-faint)", flex: "none", mt: "1px" }} />
        <p style={{ fontSize: 12.4, color: "var(--ink-faint)", margin: 0 }}>
          Painel acessível apenas para administradores ControlSIS.
        </p>
      </div>

      <AlterarLiberacaoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onUpdate={handleUpdate} empresa={empresaSelecionada} />
      <ExcluirEmpresaModal isOpen={isExcluirModalOpen} onClose={() => setIsExcluirModalOpen(false)} onExcluida={handleExcluida} empresa={empresaParaExcluir} />
    </div>
  );
};

export default PainelAdministrativo;
